import { defineStore } from 'pinia';
import lodash from 'lodash';
import { showToast } from 'vant';
import router from '../router';
import { createNewChat, getChatList, deleteChatHistory } from '../service/api';
import { getUserInfoFromStorage } from '../utils/storage';

interface PreviewImage {
    file: File;
    url: string;
}
interface ChatInfo {
    chat_name: string;
    mesList: any[];
    archived?: number;
}

interface InitMessage {
    text: string; // 初始化消息内容
    imgList: PreviewImage[]; // 初始化消息内容中的图片列表
}

const useChat = defineStore('useChat', {
    state: () => {
        return {
            // 用对象替代 Map
            chatListAll: {} as Record<number, ChatInfo>,
            currentChat: null as number | null,
            initMessage: {
                text: '',
                imgList: [] as PreviewImage[],
            } as InitMessage,
        };
    },
    getters: {
        chatListArray(state) {
            // 将对象转换为数组形式，方便 v-for 使用
            return Object.entries(state.chatListAll)
                .map(([key, value]) => [Number(key), value] as [number, ChatInfo])
                .sort((a, b) => b[0] - a[0]);
        },
    },
    actions: {
        async initNewChat(firstMessage: string, imageList: PreviewImage[] = []) {
            const res = await createNewChat();
            console.log('@@@创建新聊天返回结果', res);
            const id = res.data.chat_id;
            // 直接设置对象属性
            this.chatListAll[id] = {
                chat_name: '',
                mesList: [],
                archived: 0,
            };
            if (firstMessage !== '') {
                this.initMessage.text = firstMessage;
                this.chatListAll[id].mesList.push({
                    type: 'text',
                    content: firstMessage,
                });
            }
            if (imageList.length > 0) {
                this.initMessage.imgList = lodash.cloneDeep(imageList);
                imageList.forEach(image => {
                    this.chatListAll[id].mesList.push({
                        type: 'image',
                        content: image.url,
                    });
                });
            }
            router.push({
                path: '/chat',
                query: { chatId: id.toString() },
            });

            console.log('@@@创建新聊天', this.chatListAll);
        },
        setChatName(chatId: number, chatName: string) {
            if (this.chatListAll[chatId]) {
                this.chatListAll[chatId].chat_name = chatName;
            } else {
                console.warn(`Chat with ID ${chatId} does not exist.`);
            }
        },
        newChat() {
            this.currentChat = null;
            this.initMessage = {} as InitMessage;
            router.push({ path: '/' });
        },

        changeChat(chatId: number) {
            this.currentChat = chatId;
            router.push({
                path: '/chat',
                query: {
                    chatId,
                },
            });
            console.log('@@@切换到聊天', this.chatListAll[chatId].mesList);
        },

        async getChatListInChatStore(id?: number) {
            let userID = getUserInfoFromStorage()?.id;
            if (id) {
                userID = id;
            }
            const res = await getChatList({ user_id: userID as number });
            if (res.code !== 200) {
                showToast('获取聊天列表失败');
                return null;
            }
            return res.data.chat_list || [];
        },

        async setChatList() {
            // 获取用户信息
            const userInfo = getUserInfoFromStorage();
            if (userInfo) {
                const chatList = await this.getChatListInChatStore(userInfo.id);
                console.log('@@@获取到对话列表', chatList);

                if (chatList && chatList.length !== 0) {
                    chatList.forEach(chat => {
                        const oldChat = this.chatListAll[chat.chat_id];
                        this.chatListAll[chat.chat_id] = {
                            chat_name: chat.title || '新建聊天',
                            mesList: oldChat?.mesList ?? [],
                            archived: oldChat?.archived ?? 0,
                        };
                    });
                }
                console.log('@@@设置聊天列表', this.chatListAll);
            }
        },

        async deleteChat(id: number) {
            // 删除聊天
            const res = await deleteChatHistory({ chat_id: id });
            if (res.code !== 200) {
                showToast('删除失败，请稍后再试');
                return;
            }
            if (this.chatListAll[id]) {
                delete this.chatListAll[id];
                // 这里可以调用 API 删除聊天记录
                console.log(`@@@删除聊天 ID: ${id}`);
            } else {
                console.warn(`Chat with ID ${id} does not exist.`);
            }
            if (this.currentChat === id) {
                // 如果删除的是当前聊天，重置 currentChat
                this.newChat();
            }
        },
    },
});

export default useChat;
